import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { AuthSession } from "../models/auth-session";
import { environment } from "../../environments/environment";

const SESSION_KEY = "sdx_session";

/**
 * Persists the AuthSession in sessionStorage as AES-GCM 256-bit encrypted data.
 *
 * Encryption flow:
 *   passphrase (environment) → PBKDF2 → CryptoKey (AES-GCM 256)
 *   plaintext → encrypt(iv + ciphertext) → base64 → sessionStorage
 *
 * Decryption flow:
 *   sessionStorage → base64 → split(iv | ciphertext) → decrypt → AuthSession
 *
 * The key is derived once and cached for the lifetime of the service instance.
 * SSR-safe: all storage calls are guarded by isPlatformBrowser.
 */
@Injectable({ providedIn: "root" })
export class SessionRepositoryService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private cachedKey: CryptoKey | null = null;

  // ── Public API ────────────────────────────────────────────

  async setSession(session: AuthSession): Promise<void> {
    if (!this.isBrowser) return;
    const encrypted = await this.encrypt(JSON.stringify(session.toJson()));
    sessionStorage.setItem(SESSION_KEY, encrypted);
  }

  async getSession(): Promise<AuthSession | null> {
    if (!this.isBrowser) return null;
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) return null;
    try {
      const json = await this.decrypt(stored);
      return AuthSession.fromJson(JSON.parse(json));
    } catch {
      // Corrupted or tampered data — clear it
      this.clearSession();
      return null;
    }
  }

  clearSession(): void {
    if (!this.isBrowser) return;
    sessionStorage.removeItem(SESSION_KEY);
  }

  // ── Crypto internals ──────────────────────────────────────

  private async getKey(): Promise<CryptoKey> {
    if (this.cachedKey) return this.cachedKey;

    const enc = new TextEncoder();
    const passphrase = enc.encode(environment.sessionCryptoKey);
    const salt = enc.encode("sdx-s-stock-pbkdf2-salt-v1");

    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      passphrase,
      "PBKDF2",
      false,
      ["deriveKey"]
    );

    this.cachedKey = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt,
        iterations: 100_000,
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    return this.cachedKey;
  }

  private async encrypt(plaintext: string): Promise<string> {
    const key = await this.getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plaintext);

    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      encoded
    );

    // Prepend the IV so decrypt can recover it: [12 bytes iv | ciphertext]
    const payload = new Uint8Array(iv.byteLength + ciphertext.byteLength);
    payload.set(iv, 0);
    payload.set(new Uint8Array(ciphertext), iv.byteLength);

    return btoa(String.fromCharCode(...payload));
  }

  private async decrypt(encoded: string): Promise<string> {
    const key = await this.getKey();
    const payload = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));

    const iv = payload.slice(0, 12);
    const ciphertext = payload.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ciphertext
    );

    return new TextDecoder().decode(decrypted);
  }
}
